import { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type {
  AllowanceEntry,
  AllowancesHandler,
  DiscountHandlers,
  ElementInclusionHandler,
  ElementMarginHandler,
  PaymentMilestone,
  PaymentMilestonesHandler,
  ProjectDetailsMarginHandler,
  ProjectLayoutData,
  ProjectLayoutMode,
} from '../types';
import { capabilitiesForMode } from '../types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SectionHeading } from './SectionHeading';
import { sellPriceFromCost } from '../lib/sell-price';
import { countsTowardTcs, variationDelta } from '../lib/variation-family';

/**
 * Sprint 36 default payment-milestone template (20 / 47 / 23 / 10 %).
 * Project.paymentMilestones=null falls back to this; the editor's
 * "Reset" button restores it explicitly. Mirrors DEFAULT_MILESTONES in
 * legacy EstimateView so output matches once W31/W32 consolidate.
 */
const DEFAULT_MILESTONES: PaymentMilestone[] = [
  { ref: 1, payment: 'Down Payment',     milestone: 'Due at signing',             pct: 0.20 },
  { ref: 2, payment: 'Start Payment',    milestone: 'Due at Start of Project',    pct: 0.47 },
  { ref: 3, payment: 'Progress Payment', milestone: 'Due at Framing Inspection',  pct: 0.23 },
  { ref: 4, payment: 'Final Payment',    milestone: 'Due at Final Inspection',    pct: 0.10 },
];

/**
 * Sprint 41 §14.S10 — Section VI "Investment Summary".
 *
 * Renders computed totals plus the W22 discount rows
 * (`data.discounts`). The W22 backend emits both the line list and the
 * pre-/post-discount totals (`discountTotal`, `finalTotal` on
 * investmentSummary). This component just surfaces them and, in
 * estimate mode with a handler, adds CRUD affordances.
 *
 * Task 23 Option B — two columns (This contract | Later phases) + receipt TCS.
 * Hidden: Client’s choice + funding boxes. Label is Total Contract Sum.
 * Row Total = amount / (1 − margin%). Client sees with-margin only.
 * Project Details is always included. Administration costs stay in
 * Section II (estimator-only) and roll into the Project Details amount.
 */
export interface PaymentOption {
  id: string;
  title: string;
  amount: string;
  description: string;
}

export interface SectionVI_InvestmentSummaryProps {
  data: ProjectLayoutData;
  mode: ProjectLayoutMode;
  /**
   * Settings financing programs (title, amount, description). When
   * omitted, three placeholder boxes are derived from the cash total.
   * Rate factors stay off-screen.
   */
  paymentOptions?: PaymentOption[];
  selectedPaymentOptionId?: string;
  onPaymentOptionChange?: (id: string) => void;
  onDiscount?: DiscountHandlers;
  /**
   * W30 — when supplied AND `caps.isEditable`, the Payment Schedule
   * block exposes an Edit mode (rows editable, +/× per row, Reset to
   * defaults, Save / Cancel). Submit calls back with the milestones
   * list (or null to reset).
   */
  onPaymentMilestones?: PaymentMilestonesHandler;
  /**
   * W32 enrichment Slice 5 — when supplied AND `caps.isEditable`,
   * renders the AllowancesSummary card with add/edit/delete rows.
   * Reads from `project.allowances` (hoisted in W35-cutover Slice 1);
   * saves call back with the full array or null to clear.
   */
  onAllowances?: AllowancesHandler;
  onElementInclusion?: ElementInclusionHandler;
  onElementMargin?: ElementMarginHandler;
  onProjectDetailsMargin?: ProjectDetailsMarginHandler;
}

interface Summary {
  subtotal: string;
  overheadTotal: string;
  /** Section II Project Details rollup (permits, site costs). */
  projectDetailsTotal?: string;
  allowancesTotal?: string;
  grandTotal: string;
  discountTotal?: string;
  finalTotal?: string;
  includedElementCount?: number;
  excludedElementCount?: number;
  projectDetailsMarginPercentage?: string;
  projectDetailsSellTotal?: string;
}

interface DiscountRow {
  id: string;
  appliesTo: 'PROJECT' | 'ELEMENT';
  projectScopeElementId?: string | null;
  description: string;
  amount: string;
  isApplied: boolean;
}

interface ScopeElementSummary {
  id: string;
  elementTypeCode?: string | null;
  elementTypeDisplayName?: string | null;
  instanceLabel?: string | null;
  variationLabel?: string | null;
  parentVariationOfId?: string | null;
  clientIncluded?: boolean;
  elementTotal?: string;
  elementSubtotal?: string;
  marginPercentage?: string;
}

// Sprint 41 W32 enrichment Slice 5 — type used for parsing the
// project.allowances JSON blob (Project schema stores it loosely;
// the backend DTO accepts string|number for amount). Normalize to
// strings here so the UI's controlled-input pattern is consistent.
function normalizeAllowances(raw: unknown): AllowanceEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((e): e is Record<string, unknown> => typeof e === 'object' && e !== null)
    .map((e) => ({
      type: typeof e.type === 'string' ? e.type : '',
      description: typeof e.description === 'string' ? e.description : '',
      amount:
        typeof e.amount === 'string'
          ? e.amount
          : typeof e.amount === 'number'
            ? String(e.amount)
            : '',
    }));
}

export function SectionVI_InvestmentSummary({
  data,
  mode,
  onDiscount,
  onPaymentMilestones,
  onAllowances,
  onElementInclusion,
  onElementMargin,
  onProjectDetailsMargin,
}: SectionVI_InvestmentSummaryProps) {
  const caps = capabilitiesForMode(mode);
  const estimatorView = caps.showCostBreakdown;
  const summary = (data.investmentSummary as Summary | undefined) ?? null;
  // W22 backend emits `discounts`. Older / placeholder paths used
  // `discountLines`; tolerate both during cutover so review/proposal
  // pages that haven't been refreshed don't blank out.
  const discounts =
    ((data.discounts as DiscountRow[] | undefined) ??
      (data.discountLines as DiscountRow[] | undefined) ??
      []) as DiscountRow[];
  const scopeElements =
    ((data.scopeElements as ScopeElementSummary[] | undefined) ?? []);
  const history =
    ((data.investmentChangeHistory as Array<{ at: string; message: string }> | undefined) ??
      []);
  const project = data.project as { isFinalized?: boolean; projectDetailsMarginPercentage?: string };
  const finalized = project.isFinalized === true;

  const elementLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of scopeElements) {
      const base =
        e.variationLabel ||
        e.elementTypeDisplayName ||
        e.elementTypeCode ||
        `Element ${e.id.slice(0, 6)}`;
      m.set(e.id, e.instanceLabel && !e.variationLabel ? `${base} — ${e.instanceLabel}` : base);
    }
    return m;
  }, [scopeElements]);

  const editingEnabled = caps.isEditable && onDiscount != null;
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pdMarginDraft, setPdMarginDraft] = useState<string | null>(null);
  const [elementMarginDraft, setElementMarginDraft] = useState<Record<string, string>>({});
  const discountTotal = Number(summary?.discountTotal ?? '0');
  const projectDetailsAmount = Number(summary?.projectDetailsTotal ?? NaN);
  const persistedPdMargin = Number(
    summary?.projectDetailsMarginPercentage ??
      project.projectDetailsMarginPercentage ??
      '0',
  );
  const pdMargin = Number(pdMarginDraft ?? persistedPdMargin);
  const persistedPdSell = Number(summary?.projectDetailsSellTotal ?? NaN);
  // Client / sales-rep payloads redact projectDetailsTotal. Recompute
  // from cost only when the estimator can see (and live-edit) margin.
  const pdTotal =
    estimatorView && Number.isFinite(projectDetailsAmount)
      ? sellPriceFromCost(projectDetailsAmount, pdMargin)
      : Number.isFinite(persistedPdSell)
        ? persistedPdSell
        : sellPriceFromCost(
            Number.isFinite(projectDetailsAmount) ? projectDetailsAmount : 0,
            pdMargin,
          );

  const rows = useMemo(
    () =>
      scopeElements.map((e) => {
        const persistedMargin = Number(e.marginPercentage ?? '0');
        const margin = Number(elementMarginDraft[e.id] ?? persistedMargin);
        const persistedSell = Number(e.elementTotal ?? '0');
        const fromSubtotal = Number(e.elementSubtotal ?? NaN);
        const preMargin = Number.isFinite(fromSubtotal)
          ? fromSubtotal
          : persistedMargin > 0 && persistedMargin < 100
            ? persistedSell * (1 - persistedMargin / 100)
            : persistedSell;
        const total =
          elementMarginDraft[e.id] != null
            ? sellPriceFromCost(preMargin, margin)
            : Number.isFinite(persistedSell) && persistedSell > 0
              ? persistedSell
              : sellPriceFromCost(preMargin, margin);
        return {
          id: e.id,
          label: elementLabelById.get(e.id) ?? e.elementTypeDisplayName ?? 'Element',
          amount: preMargin,
          margin,
          total,
          included: e.clientIncluded !== false,
          parentVariationOfId: e.parentVariationOfId ?? null,
          variationLabel: e.variationLabel ?? null,
          clientIncluded: e.clientIncluded !== false,
        };
      }),
    [scopeElements, elementLabelById, elementMarginDraft],
  );

  const includedRows = rows.filter((r) => r.included);
  const tcsRows = includedRows.filter((r) => countsTowardTcs(r, rows));
  const phasedRows = rows.filter((r) => !r.included);
  const allowancesTotal = Number(summary?.allowancesTotal ?? '0');
  const rowTcs = tcsRows.reduce((sum, r) => sum + r.total, 0) + pdTotal;
  const tcs = Math.max(0, rowTcs + (Number.isFinite(allowancesTotal) ? allowancesTotal : 0) - discountTotal);
  const tcsLabel = formatUsd(tcs);

  return (
    <section
      id="section-6"
      className="scroll-mt-24"
      data-mode={mode}
      data-section="VI"
    >
      <div className="rounded-xl border border-border bg-card shadow-sm">
        <SectionHeading numeral="VI." title="Investment Summary" className="px-6 pt-5 pb-1" />
        <p className="px-6 pb-4 font-public-sans text-xs text-muted-foreground">
          {estimatorView
            ? 'Phase later keeps the work on the estimator’s tray. It is not on this contract.'
            : 'Total Contract Sum is the purchased scope with margin. Phased work is not on this contract.'}
        </p>

        <div className="space-y-4 px-6 pb-6">
        {!summary && (
          <p className="text-sm italic text-muted-foreground">
            No investment summary computed.
          </p>
        )}

        {summary && (
          <>
            {estimatorView && (
              <div
                className="grid grid-cols-1 gap-3 md:grid-cols-2"
                data-testid="section-vi-this-contract-split"
              >
                <div
                  className="overflow-hidden rounded-lg border border-border bg-card"
                  data-tray="included"
                >
                  <h3 className="border-b border-border bg-muted/40 px-3 py-2 font-public-sans text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                    This contract
                  </h3>
                  <ContractScopeRow
                    label="Project Details"
                    amount={pdTotal}
                    alwaysIncluded
                  />
                  {includedRows.map((row) => (
                    <ContractScopeRow
                      key={row.id}
                      label={row.label}
                      amount={row.total}
                      actionLabel="Phase →"
                      actionName="Phase later"
                      disabled={!onElementInclusion}
                      onAction={() => void onElementInclusion?.(row.id, false)}
                    />
                  ))}
                </div>
                <div
                  className="overflow-hidden rounded-lg border border-dashed border-border bg-muted/20"
                  data-tray="later"
                  data-testid="section-vi-later-tray"
                >
                  <h3 className="border-b border-border bg-muted/40 px-3 py-2 font-public-sans text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                    Later phases — not on this contract
                  </h3>
                  {phasedRows.length === 0 ? (
                    <p className="px-3 py-3 font-public-sans text-xs text-muted-foreground">
                      Nothing phased.
                    </p>
                  ) : (
                    phasedRows.map((row) => (
                      <ContractScopeRow
                        key={row.id}
                        label={row.label}
                        amount={row.total}
                        actionLabel="← Include"
                        actionName="Include"
                        disabled={!onElementInclusion}
                        onAction={() => void onElementInclusion?.(row.id, true)}
                      />
                    ))
                  )}
                </div>
              </div>
            )}

            <div
              className="rounded-lg border border-border bg-card px-4 py-3"
              data-testid="section-vi-total-contract-sum"
            >
              <p className="font-public-sans text-[10px] font-bold uppercase tracking-[0.08em] text-primary">
                Total Contract Sum
              </p>
              <p className="mt-1 font-newsreader text-3xl font-semibold tabular-nums text-foreground">
                {tcsLabel}
              </p>
              <ul className="mt-2 space-y-1" data-testid="section-vi-receipt-lines">
                <li className="flex items-baseline justify-between gap-3 border-b border-border/60 py-1 font-public-sans text-sm">
                  <span>Project Details</span>
                  <span className="tabular-nums">{formatUsd(pdTotal)}</span>
                </li>
                {includedRows.map((row) => {
                  const delta = variationDelta(row, rows);
                  return (
                    <li
                      key={row.id}
                      className="flex items-baseline justify-between gap-3 border-b border-border/60 py-1 font-public-sans text-sm"
                    >
                      <span>{row.label}</span>
                      <span className="tabular-nums">
                        {formatUsd(row.total)}
                        {delta != null ? (
                          <span className="ml-2 text-muted-foreground">
                            ({formatSignedUsd(delta)})
                          </span>
                        ) : null}
                      </span>
                    </li>
                  );
                })}
                {discountTotal > 0 && (
                  <li className="flex items-baseline justify-between gap-3 border-b border-border/60 py-1 font-public-sans text-sm text-foreground-soft">
                    <span>Discount</span>
                    <span className="tabular-nums">-{formatUsd(discountTotal)}</span>
                  </li>
                )}
              </ul>
            </div>

            {estimatorView && (
              <div className="overflow-x-auto" data-testid="section-vi-ledger">
                <table className="w-full border-collapse font-public-sans text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      <th className="py-2 pr-3">Line</th>
                      <th className="py-2 pr-3 text-right">Amount</th>
                      <th className="py-2 pr-3 text-right">Margin</th>
                      <th className="py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr data-testid="section-vi-project-details-total">
                      <td className="py-2 pr-3">Project Details</td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {formatUsd(projectDetailsAmount)}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <label className="inline-flex items-center justify-end gap-1">
                          <Input
                            type="number"
                            min={0}
                            max={99}
                            step={1}
                            aria-label="Project Details margin"
                            disabled={finalized || !onProjectDetailsMargin}
                            value={pdMarginDraft ?? String(persistedPdMargin || 0)}
                            onChange={(e) => setPdMarginDraft(e.target.value)}
                            onBlur={() => {
                              if (!onProjectDetailsMargin || pdMarginDraft == null) return;
                              const next = Number(pdMarginDraft);
                              if (!Number.isFinite(next) || next < 0 || next >= 100) {
                                setPdMarginDraft(null);
                                return;
                              }
                              void onProjectDetailsMargin(String(next));
                              setPdMarginDraft(null);
                            }}
                            className="h-7 w-16 text-right"
                          />
                          <span>%</span>
                        </label>
                      </td>
                      <td className="py-2 text-right tabular-nums">{formatUsd(pdTotal)}</td>
                    </tr>
                    {includedRows.map((row) => (
                      <tr key={row.id} data-row={row.id}>
                        <td className="py-2 pr-3">{row.label}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {formatUsd(row.amount)}
                        </td>
                        <td className="py-2 pr-3 text-right">
                          <label className="inline-flex items-center justify-end gap-1">
                            <Input
                              type="number"
                              min={0}
                              max={99}
                              step={1}
                              aria-label={`${row.label} margin`}
                              disabled={finalized || !onElementMargin}
                              value={elementMarginDraft[row.id] ?? String(row.margin || 0)}
                              onChange={(e) =>
                                setElementMarginDraft((prev) => ({
                                  ...prev,
                                  [row.id]: e.target.value,
                                }))
                              }
                              onBlur={() => {
                                const draft = elementMarginDraft[row.id];
                                if (!onElementMargin || draft == null) return;
                                const next = Number(draft);
                                if (!Number.isFinite(next) || next < 0 || next >= 100) {
                                  setElementMarginDraft((prev) => {
                                    const copy = { ...prev };
                                    delete copy[row.id];
                                    return copy;
                                  });
                                  return;
                                }
                                void onElementMargin(row.id, String(next));
                                setElementMarginDraft((prev) => {
                                  const copy = { ...prev };
                                  delete copy[row.id];
                                  return copy;
                                });
                              }}
                              className="h-7 w-16 text-right"
                            />
                            <span>%</span>
                          </label>
                        </td>
                        <td className="py-2 text-right tabular-nums">{formatUsd(row.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {estimatorView && (discounts.length > 0 || editingEnabled) && (
              <div className="space-y-1.5" data-testid="section-vi-discount-line">
                {discounts.length === 0 && !adding && (
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="italic text-sm text-muted-foreground">
                      No discounts applied
                    </span>
                    <span className="font-mono text-sm text-foreground-soft">
                      {formatUsd(summary.discountTotal ?? '0')}
                    </span>
                  </div>
                )}
                {discounts.map((d) =>
                  editingId === d.id && onDiscount ? (
                    <DiscountForm
                      key={d.id}
                      initial={d}
                      scopeElements={scopeElements}
                      onCancel={() => setEditingId(null)}
                      onCommit={async (payload) => {
                        await onDiscount.onUpdate(d.id, payload);
                        setEditingId(null);
                      }}
                    />
                  ) : (
                    <DiscountLine
                      key={d.id}
                      discount={d}
                      elementLabel={
                        d.appliesTo === 'ELEMENT' && d.projectScopeElementId
                          ? elementLabelById.get(d.projectScopeElementId) ?? null
                          : null
                      }
                      editable={editingEnabled}
                      onEdit={() => {
                        setAdding(false);
                        setEditingId(d.id);
                      }}
                      onDelete={() => {
                        if (
                          onDiscount &&
                          confirm(`Delete discount "${d.description}"?`)
                        ) {
                          void onDiscount.onDelete(d.id);
                        }
                      }}
                    />
                  ),
                )}
                {editingEnabled && !adding && (
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto px-1 py-0.5 text-xs font-semibold text-primary hover:text-primary-dim"
                      onClick={() => {
                        setEditingId(null);
                        setAdding(true);
                      }}
                    >
                      + Add discount
                    </Button>
                  </div>
                )}
                {adding && onDiscount && (
                  <DiscountForm
                    scopeElements={scopeElements}
                    onCancel={() => setAdding(false)}
                    onCommit={async (payload) => {
                      await onDiscount.onAdd(payload);
                      setAdding(false);
                    }}
                  />
                )}
              </div>
            )}

            {estimatorView && history.length > 0 && (
              <div className="rounded-md border border-border bg-muted/20 px-4 py-3" data-testid="section-vi-change-history">
                <h3 className="font-public-sans text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                  Change history
                </h3>
                <ol className="mt-2 list-decimal space-y-1 pl-5 font-public-sans text-xs text-foreground-soft">
                  {history.map((entry, i) => (
                    <li key={`${entry.at}-${i}`}>
                      {formatHistoryStamp(entry.at)} — {entry.message}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </>
        )}

        {/* Founder 2026-08-28 — stack Payment Milestones above Allowances
            at full width. Side-by-side squeezed the milestones table. */}
        <div
          className="space-y-6"
          data-testid="section-vi-schedule-allowances"
          data-layout="stacked"
        >
          <PaymentScheduleBlock
            scheduleTotalForRender={String(tcs)}
            milestones={
              (data.project.paymentMilestones as PaymentMilestone[] | null | undefined) ?? null
            }
            editable={caps.isEditable && onPaymentMilestones != null}
            onSave={onPaymentMilestones}
          />

          {/* W32 enrichment Slice 5 — Allowances list. Editor when
              caps.isEditable + handler supplied; otherwise read-only card.
              Hidden entirely when the list is empty/null in read-only modes. */}
          <AllowancesBlock
            allowances={normalizeAllowances(
              data.project.allowances ??
                (data as { allowances?: unknown }).allowances,
            )}
            editable={caps.isEditable && onAllowances != null}
            onSave={onAllowances}
          />
        </div>
        </div>
      </div>
    </section>
  );
}

function ContractScopeRow({
  label,
  amount,
  alwaysIncluded = false,
  actionLabel,
  actionName,
  disabled,
  onAction,
}: {
  label: string;
  amount: number;
  alwaysIncluded?: boolean;
  actionLabel?: string;
  actionName?: string;
  disabled?: boolean;
  onAction?: () => void;
}) {
  return (
    <div
      className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2 border-b border-border px-3 py-2 last:border-b-0"
      data-chip={label}
    >
      <div className="min-w-0">
        <p className="font-public-sans text-sm text-foreground">
          {alwaysIncluded ? <strong>{label}</strong> : label}
        </p>
        {alwaysIncluded && (
          <p className="font-public-sans text-[11px] font-semibold text-primary">Always included</p>
        )}
      </div>
      <span className="font-public-sans text-sm font-semibold tabular-nums text-foreground">
        {formatUsd(amount)}
      </span>
      {actionLabel && onAction ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          aria-label={actionName ?? actionLabel}
          onClick={onAction}
          className="h-auto px-1.5 py-1 font-public-sans text-[11px] font-bold text-primary hover:text-primary-dim"
        >
          {actionLabel}
        </Button>
      ) : (
        <span />
      )}
    </div>
  );
}

function formatHistoryStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function DiscountLine({
  discount,
  elementLabel,
  editable,
  onEdit,
  onDelete,
}: {
  discount: DiscountRow;
  elementLabel: string | null;
  editable: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const muted = !discount.isApplied;
  return (
    <div
      className={`text-sm ${muted ? 'opacity-50' : ''}`}
      data-discount-id={discount.id}
      data-applies-to={discount.appliesTo}
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className="font-public-sans text-foreground">
          {discount.description}
          {!discount.isApplied && (
            <span
              className="ml-2 rounded bg-card px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground"
              title="Excluded element — not subtracted from totals"
            >
              not applied
            </span>
          )}
        </p>
        <div className="flex shrink-0 items-baseline gap-2">
          <span className="font-mono text-sm text-foreground-soft">
            -{formatUsd(discount.amount)}
          </span>
          {editable && (
            <>
              <Button type="button" variant="ghost" size="sm"
                aria-label="Edit discount"
                className="h-auto px-1 py-0 text-muted-foreground hover:text-primary"
                onClick={onEdit}>
                ✎
              </Button>
              <Button type="button" variant="ghost" size="sm"
                aria-label="Delete discount"
                className="h-auto px-1 py-0 text-muted-foreground hover:text-destructive"
                onClick={onDelete}>
                ×
              </Button>
            </>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        {discount.appliesTo === 'PROJECT'
          ? 'Project-level'
          : `Element: ${elementLabel ?? '(unknown)'}`}
      </p>
    </div>
  );
}

function DiscountForm({
  initial,
  scopeElements,
  onCommit,
  onCancel,
}: {
  initial?: DiscountRow;
  scopeElements: ScopeElementSummary[];
  onCommit: (payload: {
    appliesTo: 'PROJECT' | 'ELEMENT';
    description: string;
    amount: string;
    projectScopeElementId?: string | null;
  }) => Promise<void>;
  onCancel: () => void;
}) {
  const [appliesTo, setAppliesTo] = useState<'PROJECT' | 'ELEMENT'>(
    initial?.appliesTo ?? 'PROJECT',
  );
  const [description, setDescription] = useState(initial?.description ?? '');
  const [amount, setAmount] = useState(initial?.amount ?? '');
  const [elementId, setElementId] = useState<string>(
    initial?.projectScopeElementId ?? '',
  );
  const [busy, setBusy] = useState(false);

  const valid =
    description.trim().length > 0 &&
    isNumericString(amount.trim()) &&
    (appliesTo === 'PROJECT' || elementId !== '');

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await onCommit({
        appliesTo,
        description: description.trim(),
        amount: amount.trim(),
        projectScopeElementId:
          appliesTo === 'ELEMENT' ? elementId : null,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-wrap items-center gap-2 rounded border border-outline-variant bg-surface-low p-2 text-xs"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <label className="flex items-center gap-1">
        <span className="font-semibold text-foreground-soft">Scope:</span>
        <Select
          disabled={busy}
          value={appliesTo}
          onValueChange={(v) => setAppliesTo(v as 'PROJECT' | 'ELEMENT')}
        >
          <SelectTrigger className="h-7 w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="PROJECT">Project</SelectItem>
            <SelectItem value="ELEMENT">Element</SelectItem>
          </SelectContent>
        </Select>
      </label>
      {appliesTo === 'ELEMENT' && (
        <Select disabled={busy} value={elementId} onValueChange={setElementId}>
          <SelectTrigger className="h-7 w-56" aria-label="Target element">
            <SelectValue placeholder="(pick an element)" />
          </SelectTrigger>
          <SelectContent>
            {scopeElements.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                {e.elementTypeDisplayName ?? e.elementTypeCode ?? e.id.slice(0, 6)}
                {e.instanceLabel ? ` — ${e.instanceLabel}` : ''}
                {e.clientIncluded === false ? ' (excluded)' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Input
        autoFocus
        disabled={busy}
        value={description}
        placeholder="Description"
        aria-label="Discount description"
        className="flex-1 rounded border border-outline-variant bg-card px-1.5 py-0.5"
        onChange={(e) => setDescription(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
        }}
      />
      <Input
        disabled={busy}
        value={amount}
        placeholder="0.00"
        aria-label="Discount amount"
        className="w-24 rounded border border-outline-variant bg-card px-1.5 py-0.5 text-right font-mono"
        onChange={(e) => setAmount(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
        }}
      />
      <Button type="submit" size="sm"
        disabled={!valid || busy}
        className="h-7 rounded px-2 py-0.5 text-xs font-semibold">
        {busy ? 'Saving…' : 'Save'}
      </Button>
      <Button type="button" variant="ghost" size="sm"
        disabled={busy}
        onClick={onCancel}
        className="h-7 rounded px-2 py-0.5 text-xs text-foreground-soft hover:bg-surface hover:text-foreground">
        Cancel
      </Button>
    </form>
  );
}

function isNumericString(s: string): boolean {
  if (!s) return false;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0;
}

/**
 * Sprint 41 W30 — Payment Schedule block. Mirrors the legacy
 * PaymentMilestonesCard in EstimateView; lives inside the umbrella now
 * so the V2 Estimate view + future Proposal / Contract pages all see
 * the same schedule. Read-only when `editable` is false.
 *
 * pct rows are stored as fractions (0.20). Editor surfaces them as
 * percentages (20.00) for human input.
 */
function PaymentScheduleBlock({
  scheduleTotalForRender,
  milestones,
  editable,
  onSave,
}: {
  scheduleTotalForRender: string;
  milestones: PaymentMilestone[] | null;
  editable: boolean;
  onSave?: PaymentMilestonesHandler;
}) {
  const persisted: PaymentMilestone[] =
    milestones && milestones.length > 0 ? milestones : DEFAULT_MILESTONES;
  const isUsingDefault = milestones == null || milestones.length === 0;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<PaymentMilestone[]>(persisted);
  const [busy, setBusy] = useState(false);

  // Sync draft back to persisted whenever the resolver returns new data
  // (e.g., after a Save reload). Keyed on stable identity, not the
  // outer milestones reference, so editing isn't clobbered mid-flight.
  const persistedKey = JSON.stringify(persisted);
  useEffect(() => {
    if (!editing) setDraft(persisted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistedKey, editing]);

  const total = Number(scheduleTotalForRender) || 0;
  const draftPctSum = draft.reduce((s, m) => s + (Number(m.pct) || 0), 0);
  const pctOff = Math.abs(draftPctSum - 1) > 0.0001;
  const rows = editing ? draft : persisted;

  const updateRow = (idx: number, patch: Partial<PaymentMilestone>) => {
    setDraft((prev) => prev.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  };
  const addRow = () => {
    setDraft((prev) => [
      ...prev,
      { ref: prev.length + 1, payment: '', milestone: '', pct: 0 },
    ]);
  };
  const removeRow = (idx: number) => {
    setDraft((prev) =>
      prev.filter((_, i) => i !== idx).map((m, i) => ({ ...m, ref: i + 1 })),
    );
  };
  const resetToDefaults = () => setDraft(DEFAULT_MILESTONES);
  const save = async () => {
    if (!onSave || busy || pctOff) return;
    setBusy(true);
    try {
      await onSave(draft);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };
  const resetPersisted = async () => {
    if (!onSave || busy) return;
    if (!confirm('Reset payment schedule to the default 20/47/23/10% template?')) {
      return;
    }
    setBusy(true);
    try {
      await onSave(null);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-block="payment-schedule">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-newsreader text-base font-semibold text-foreground">
          Payment Milestones
        </h3>
        {editable && (
          <div className="flex items-baseline gap-3 text-sm">
            {editing ? (
              <>
                <Button type="button" variant="ghost" size="sm"
                  onClick={resetToDefaults}
                  disabled={busy}
                  className="h-auto px-1 py-0.5 text-xs font-semibold text-foreground-soft hover:text-destructive disabled:opacity-50"
                  title="Reset draft to default 20/47/23/10%">
                  Reset to default
                </Button>
                <Button type="button" variant="ghost" size="sm"
                  onClick={() => {
                    setEditing(false);
                    setDraft(persisted);
                  }}
                  disabled={busy}
                  className="h-auto px-1 py-0.5 text-xs text-foreground-soft hover:text-foreground disabled:opacity-50"
                >
                  Cancel
                </Button>
                <Button type="button" size="sm"
                  onClick={() => void save()}
                  disabled={busy || pctOff}
                  className="h-7 px-2 py-0.5 text-xs font-semibold"
                  title={pctOff ? 'Percentages must sum to 100%' : undefined}
                >
                  {busy ? 'Saving…' : 'Save'}
                </Button>
              </>
            ) : (
              <>
                {!isUsingDefault && (
                  <Button type="button" variant="ghost" size="sm"
                    onClick={() => void resetPersisted()}
                    disabled={busy}
                    className="h-auto px-1 py-0.5 text-xs font-semibold text-foreground-soft hover:text-destructive disabled:opacity-50"
                    title="Clear the project override and revert to the default template"
                  >
                    Reset to default
                  </Button>
                )}
                <Button type="button" variant="ghost" size="sm"
                  onClick={() => setEditing(true)}
                  disabled={busy}
                  className="h-auto px-1 py-0.5 text-xs font-semibold text-primary hover:text-primary-dim disabled:opacity-50"
                >
                  Edit
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="mt-2 overflow-hidden rounded-md border border-border">
      <table className="w-full border-collapse font-public-sans text-sm">
        <thead>
          <tr className="bg-surface-low">
            <th className="w-8 border-b border-outline-variant px-2 py-1 text-left text-[10px] font-bold uppercase tracking-wider text-foreground-soft">
              #
            </th>
            <th className="border-b border-outline-variant px-2 py-1 text-left text-[10px] font-bold uppercase tracking-wider text-foreground-soft">
              Payment
            </th>
            <th className="border-b border-outline-variant px-2 py-1 text-left text-[10px] font-bold uppercase tracking-wider text-foreground-soft">
              Milestone
            </th>
            <th className="w-24 border-b border-outline-variant px-2 py-1 text-right text-[10px] font-bold uppercase tracking-wider text-foreground-soft">
              Percentage
            </th>
            <th className="w-32 border-b border-outline-variant px-2 py-1 text-right text-[10px] font-bold uppercase tracking-wider text-foreground-soft">
              Amount
            </th>
            {editing && <th className="w-8 border-b border-outline-variant" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((m, idx) => (
            <tr key={`${m.ref}-${idx}`}>
              <td className="border-b border-outline-variant px-2 py-1 font-semibold tabular-nums text-foreground">
                {m.ref}
              </td>
              <td className="border-b border-outline-variant px-2 py-1 font-medium text-foreground">
                {editing ? (
                  <Input
                    value={m.payment}
                    onChange={(e) => updateRow(idx, { payment: e.target.value })}
                    className="w-full border-b border-outline-variant bg-transparent focus:border-border focus:outline-none"
                  />
                ) : (
                  m.payment
                )}
              </td>
              <td className="border-b border-outline-variant px-2 py-1 text-foreground-soft">
                {editing ? (
                  <Input
                    value={m.milestone}
                    onChange={(e) => updateRow(idx, { milestone: e.target.value })}
                    className="w-full border-b border-outline-variant bg-transparent focus:border-border focus:outline-none"
                  />
                ) : (
                  m.milestone
                )}
              </td>
              <td className="border-b border-outline-variant px-2 py-1 text-right tabular-nums">
                {editing ? (
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step={0.01}
                    value={(m.pct * 100).toFixed(2)}
                    onChange={(e) =>
                      updateRow(idx, {
                        pct: (Number(e.target.value) || 0) / 100,
                      })
                    }
                    className="w-16 border-b border-outline-variant bg-transparent text-right focus:border-border focus:outline-none"
                  />
                ) : (
                  `${(m.pct * 100).toFixed(2)}%`
                )}
              </td>
              <td className="border-b border-outline-variant px-2 py-1 text-right font-mono font-semibold tabular-nums text-foreground">
                {formatUsd(total * m.pct)}
              </td>
              {editing && (
                <td className="border-b border-outline-variant px-1 py-1 text-right">
                  <Button type="button" variant="ghost" size="sm"
                    aria-label="Remove milestone"
                    onClick={() => removeRow(idx)}
                    className="h-auto px-1 py-0 text-muted-foreground hover:text-destructive"
                  >
                    ×
                  </Button>
                </td>
              )}
            </tr>
          ))}
          {editing && (
            <tr>
              <td colSpan={6} className="px-2 py-1 text-right">
                <Button type="button" variant="ghost" size="sm"
                  onClick={addRow}
                  className="h-auto px-1 py-0.5 text-xs font-semibold text-primary hover:text-primary-dim">
                  + Add milestone
                </Button>
              </td>
            </tr>
          )}
        </tbody>
        {editing && (
          <tfoot>
            <tr>
              <td colSpan={3} className="px-2 py-1 text-right text-[10px] uppercase tracking-wider text-muted-foreground">
                Total
              </td>
              <td
                className={`px-2 py-1 text-right tabular-nums ${
                  pctOff ? 'font-semibold text-red-600' : 'text-foreground-soft'
                }`}
                title={pctOff ? 'Must sum to 100%' : undefined}
              >
                {(draftPctSum * 100).toFixed(2)}%
              </td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        )}
      </table>
      {!editing && (
        <p className="px-4 py-2 text-[11px] italic text-muted-foreground">
          Progress payment milestones may be reached in a different order depending
          on the changes in the sequence in which work is performed.
        </p>
      )}
      </div>
    </div>
  );
}

/**
 * Format-fix batch 2026-07 (finding 19) — Section VI money renders as
 * currency (2 decimals, thousands separators) instead of the payload's
 * raw decimal strings ("2286.9"). Display-only: inputs keep raw values.
 */
function formatUsd(v: string | number): string {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatSignedUsd(n: number): string {
  const abs = formatUsd(Math.abs(n));
  if (n > 0) return `+${abs}`;
  if (n < 0) return `−${abs}`;
  return abs;
}

/**
 * Sprint 41 W32 enrichment Slice 5 — Section VI AllowancesSummary.
 *
 * Renders:
 *   - read-only card listing allowances when at least one entry exists.
 *     Nothing rendered when the list is empty/null in read-only modes.
 *   - editor table (type / description / amount) + Add row + Save /
 *     Cancel / Clear-all buttons when `caps.isEditable && onSave`.
 *
 * Save semantics:
 *   - non-empty list  → POST array of entries
 *   - empty list save → POST [] (treated by backend as "no allowances")
 *   - Clear all       → POST null (explicit clear, distinguishable from
 *                       "never set" in the audit trail)
 *
 * Amount is kept as a string in the controlled input. Backend accepts
 * string|number for amount; we send strings unchanged.
 */
function AllowancesBlock({
  allowances,
  editable,
  onSave,
}: {
  allowances: AllowanceEntry[];
  editable: boolean;
  onSave?: AllowancesHandler;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<AllowanceEntry[]>(allowances);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) setDraft(allowances);
  }, [allowances, editing]);

  if (!editable) {
    if (allowances.length === 0) return null;
    return (
      <div data-section-block="allowances">
        <h3 className="font-newsreader text-base font-semibold text-foreground">
          Allowances
        </h3>
        <p className="mt-0.5 font-public-sans text-xs text-muted-foreground">
          Budgeted amounts for items not yet finalized. Amounts shown are included
          in the grand total above.
        </p>
        <AllowancesReadTable allowances={allowances} testId="section-vi-allowances-read" />
      </div>
    );
  }

  const handleAdd = () => {
    setDraft((d) => [...d, { type: '', description: '', amount: '' }]);
  };
  const handleUpdate = (i: number, field: keyof AllowanceEntry, value: string) => {
    setDraft((d) => d.map((e, idx) => (idx === i ? { ...e, [field]: value } : e)));
  };
  const handleDelete = (i: number) => {
    setDraft((d) => d.filter((_, idx) => idx !== i));
  };
  const cancel = () => {
    setDraft(allowances);
    setEditing(false);
    setError(null);
  };
  const save = async () => {
    if (!onSave || busy) return;
    setBusy(true);
    setError(null);
    try {
      // Filter rows that are entirely empty before sending.
      const cleaned = draft.filter(
        (e) => e.type.trim() !== '' || e.description.trim() !== '' || e.amount.trim() !== '',
      );
      await onSave(cleaned);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save allowances');
    } finally {
      setBusy(false);
    }
  };
  const clearAll = async () => {
    if (!onSave || busy) return;
    if (!confirm('Clear all manual allowances on this project?')) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(null);
      setDraft([]);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to clear allowances');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-section-block="allowances">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h3 className="font-newsreader text-base font-semibold text-foreground">
            Allowances
          </h3>
          <p className="mt-0.5 font-public-sans text-xs text-muted-foreground">
            Budgeted amounts for items the client has not yet finalized. Amounts
            shown are included in the grand total above.
          </p>
        </div>
        {!editing && (
          <div className="flex items-baseline gap-3 text-sm">
            {allowances.length > 0 && (
              <Button type="button" variant="ghost" size="sm"
                onClick={clearAll}
                disabled={busy}
                className="h-auto px-1 py-0.5 text-sm font-semibold text-foreground-soft hover:text-destructive disabled:opacity-50">
                Clear all
              </Button>
            )}
            <Button type="button" variant="ghost" size="sm"
              onClick={() => setEditing(true)}
              disabled={busy}
              className="h-auto px-1 py-0.5 text-sm font-semibold text-primary hover:text-primary-dim disabled:opacity-50"
            >
              {allowances.length > 0 ? 'Edit' : 'Add allowances'}
            </Button>
          </div>
        )}
      </div>
      {editing ? (
        <>
          {draft.length === 0 ? (
            <p className="mt-3 text-sm italic text-muted-foreground">
              No allowances yet. Click <strong>Add row</strong> to start.
            </p>
          ) : (
            <table className="mt-3 w-full text-sm">
              <thead>
                <tr className="border-b border-outline-variant text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  <th className="py-2 pr-3">Purpose</th>
                  <th className="py-2 pr-3">Description</th>
                  <th className="py-2 pr-3 text-right">Amount</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {draft.map((a, i) => (
                  <tr key={i} className="border-b border-outline-variant last:border-b-0">
                    <td className="py-1 pr-3">
                      <Input
                        value={a.type}
                        onChange={(e) => handleUpdate(i, 'type', e.target.value)}
                        placeholder="e.g. Permits & inspections"
                        className="w-full border border-outline-variant px-2 py-1 font-public-sans text-sm focus:border-border-subtle focus:outline-none"
                      />
                    </td>
                    <td className="py-1 pr-3">
                      <Input
                        value={a.description}
                        onChange={(e) => handleUpdate(i, 'description', e.target.value)}
                        placeholder="e.g. Cabinet hardware"
                        className="w-full border border-outline-variant px-2 py-1 font-public-sans text-sm focus:border-border-subtle focus:outline-none"
                      />
                    </td>
                    <td className="py-1 pr-3">
                      <Input
                        value={a.amount}
                        onChange={(e) => handleUpdate(i, 'amount', e.target.value)}
                        placeholder="0"
                        className="w-full border border-outline-variant px-2 py-1 text-right font-public-sans text-sm focus:border-border-subtle focus:outline-none"
                      />
                    </td>
                    <td className="py-1 text-right">
                      <Button type="button" variant="ghost" size="sm"
                        onClick={() => handleDelete(i)}
                        className="h-auto px-1 py-0 text-muted-foreground hover:text-destructive"
                        aria-label="Remove row"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="mt-3 flex items-center justify-between">
            <Button type="button" variant="ghost" size="sm"
              onClick={handleAdd}
              disabled={busy}
              className="h-auto gap-1.5 px-1 py-0.5 text-sm font-semibold text-primary hover:text-primary-dim disabled:opacity-50">
              <Plus className="h-3 w-3" /> Add row
            </Button>
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" size="sm"
                onClick={cancel}
                disabled={busy}
                className="text-foreground-soft hover:bg-surface-low hover:text-foreground disabled:opacity-50">
                Cancel
              </Button>
              <Button type="button" size="sm"
                onClick={() => void save()}
                disabled={busy}
                className="gap-2"
              >
                {busy && <Loader2 className="h-3 w-3 animate-spin" />}
                {busy ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
          {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
        </>
      ) : allowances.length === 0 ? (
        <p className="mt-3 text-sm italic text-muted-foreground">
          No allowances on this project. Click <strong>Add allowances</strong> to itemize fixed dollar amounts for client choice (e.g., cabinet hardware, finish upgrades).
        </p>
      ) : (
        <AllowancesReadTable allowances={allowances} />
      )}
    </div>
  );
}

/**
 * Founder mockup 2026-07-16 — read-only Allowances table (Purpose /
 * Description / Amount + ALLOWANCES SUBTOTAL footer). Shared by the
 * read-only modes and the estimate-mode view state.
 */
function AllowancesReadTable({
  allowances,
  testId,
}: {
  allowances: AllowanceEntry[];
  testId?: string;
}) {
  const subtotal = allowances.reduce((sum, a) => {
    const n = Number(a.amount);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
  return (
    <table className="mt-3 w-full text-sm" data-testid={testId}>
      <thead>
        <tr className="border-b border-outline-variant text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          <th className="py-2 pr-3">Purpose</th>
          <th className="py-2 pr-3">Description</th>
          <th className="py-2 text-right">Amount</th>
        </tr>
      </thead>
      <tbody>
        {allowances.map((a, i) => (
          <tr key={i} className="border-b border-outline-variant last:border-b-0">
            <td className="py-2 pr-3 font-public-sans font-medium text-foreground">{a.type || '—'}</td>
            <td className="py-2 pr-3 font-public-sans text-foreground-soft">{a.description || '—'}</td>
            <td className="py-2 text-right font-mono text-foreground">
              {a.amount ? formatUsd(a.amount) : '—'}
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="border-t border-outline-variant">
          <td colSpan={2} className="py-2 pr-3 font-public-sans text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
            Allowances subtotal
          </td>
          <td className="py-2 text-right font-mono font-semibold text-foreground">
            {formatUsd(subtotal)}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}
